import React, { useEffect, useRef, useState } from "react"
import { Badge, Button, Col, Form, ListGroup } from "react-bootstrap"
import { MessageSquare, Plus, Trash2 } from "lucide-react"
import Swal from "sweetalert2"
import { useTranslation } from "react-i18next"

/* ─── Tipos ─────────────────────────────────────────────────────────────── */

export type NoteUser = {
  _id?: string
  name?: string
}

export type NoteReply = {
  _id?: string
  ebrokerId?: number
  source?: 'app' | 'ebroker'
  text?: string
  userName?: string
  user?: NoteUser
  date?: string | Date
}

export type NoteThread = {
  _id?: string
  ebrokerId?: number
  source?: 'app' | 'ebroker'
  titulo?: string
  text?: string
  userName?: string
  user?: NoteUser
  date?: string | Date
  lastActivity?: string | Date
  notes?: NoteReply[]
}

interface NotesPanelProps {
  value: NoteThread[]
  onChange: (notes: NoteThread[]) => void
  currentUserId?: string
}

/* ─── Helpers de estilos ─────────────────────────────────────────────────── */

const bubbleBase: React.CSSProperties = {
  maxWidth: '80%',
  padding: '10px 14px',
  borderRadius: '16px',
  fontSize: '0.875rem',
  lineHeight: '1.45',
  wordBreak: 'break-word',
}

const bubbleEbroker: React.CSSProperties = {
  ...bubbleBase,
  background: '#f0f2f5',
  color: '#1c1e21',
  borderTopLeftRadius: 4,
}

const bubbleApp: React.CSSProperties = {
  ...bubbleBase,
  background: '#0d6efd',
  color: '#fff',
  borderTopRightRadius: 4,
}

const bubbleNew: React.CSSProperties = {
  ...bubbleBase,
  maxWidth: '100%',
  background: '#fff8e1',
  border: '1.5px dashed #ffc107',
  borderRadius: '12px',
}

/* ─── Componente principal ──────────────────────────────────────────────── */

export default function NotesPanel({
  value = [],
  onChange,
  currentUserId,
}: NotesPanelProps) {
  const { t } = useTranslation()
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)

  const notes: NoteThread[] = value
  const selectedNote: NoteThread | null =
    selectedIndex !== null ? notes[selectedIndex] : null

  /* ── Helpers ── */

  const isEditable = (item?: { _id?: string; ebrokerId?: number; source?: string }) =>
    !item?._id && item?.ebrokerId == null && item?.source !== 'ebroker'

  const isFromEbroker = (item?: { source?: string }) =>
    item?.source === 'ebroker'

  const authorName = (item?: { user?: NoteUser; userName?: string }) =>
    item?.user?.name || item?.userName || ''

  const formatDate = (date?: string | Date) => {
    if (!date) return ""
    const d = new Date(date)
    if (isNaN(d.getTime())) return ""
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    })
  }

  const updateNotes = (updater: (prev: NoteThread[]) => NoteThread[]) => {
    const cloned = structuredClone(notes)
    onChange(updater(cloned))
  }

  /* ── Hilos ── */

  const addThread = () => {
    updateNotes(prev => [
      ...prev,
      { titulo: "", text: "", source: 'app', user: { _id: currentUserId }, notes: [], date: new Date(), lastActivity: new Date() },
    ])
    setSelectedIndex(notes.length)
  }

  const updateThreadTitulo = (v: string) => {
    if (selectedIndex === null) return
    updateNotes(prev => { prev[selectedIndex].titulo = v; return prev })
  }

  const updateThreadText = (v: string) => {
    if (selectedIndex === null) return
    updateNotes(prev => {
      prev[selectedIndex].text = v
      prev[selectedIndex].lastActivity = new Date()
      return prev
    })
  }

  const deleteThread = async (index: number) => {
    const res = await Swal.fire({
      title: t("¿Eliminar conversación?"),
      text: t("Se eliminará el hilo y todas sus respuestas."),
      icon: "warning", showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: t("Eliminar"), cancelButtonText: t("Cancelar"),
    })
    if (!res.isConfirmed) return
    updateNotes(prev => { prev.splice(index, 1); return prev })
    setSelectedIndex(null)
  }

  /* ── Respuestas ── */

  const addReply = () => {
    if (selectedIndex === null) return
    updateNotes(prev => {
      prev[selectedIndex].notes!.push({ text: "", source: 'app', user: { _id: currentUserId }, date: new Date() })
      prev[selectedIndex].lastActivity = new Date()
      return prev
    })
  }

  const updateReplyText = (ri: number, v: string) => {
    if (selectedIndex === null) return
    updateNotes(prev => {
      prev[selectedIndex].notes![ri].text = v
      prev[selectedIndex].lastActivity = new Date()
      return prev
    })
  }

  const deleteReply = async (ri: number) => {
    if (selectedIndex === null) return
    const res = await Swal.fire({
      title: t("¿Eliminar respuesta?"), icon: "warning", showCancelButton: true,
      confirmButtonText: t("Eliminar"), cancelButtonText: t("Cancelar"),
    })
    if (!res.isConfirmed) return
    updateNotes(prev => {
      prev[selectedIndex].notes!.splice(ri, 1)
      prev[selectedIndex].lastActivity = new Date()
      return prev
    })
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [selectedNote?.notes?.length])

  /* Mantener la selección cuando la lista se recarga tras guardar:
     el backend devuelve _id/ebrokerId y puede reordenar por actividad.
     Sin esto, el índice apuntaría a otro hilo y parecería que
     el mensaje sigue editable. Se compara contra el array previo
     (ref), no contra el ya actualizado. */
  const prevNotesRef = useRef<NoteThread[]>(notes)
  useEffect(() => {
    const prevNotes = prevNotesRef.current
    if (selectedIndex !== null && prevNotes !== notes) {
      const prevSelected = prevNotes[selectedIndex]
      const cur = selectedIndex < notes.length ? notes[selectedIndex] : undefined
      const keyOf = (n?: NoteThread | null) =>
        n ? (n._id || (n.ebrokerId != null ? `eb:${n.ebrokerId}` : null)) : null
      const contentOf = (n?: NoteThread | null) =>
        n ? `${n.titulo || ''}||${n.text || ''}` : null
      const prevKey = keyOf(prevSelected)
      const curKey = keyOf(cur)
      const same =
        (prevKey && curKey && prevKey === curKey) ||
        (!prevKey && !curKey && prevSelected && cur && contentOf(prevSelected) === contentOf(cur))
      if (!same) {
        const prevContent = contentOf(prevSelected)
        let found = -1
        if (prevKey) {
          found = notes.findIndex(n => {
            const k = keyOf(n)
            return k != null && k === prevKey
          })
        }
        if (found === -1 && prevContent) {
          found = notes.findIndex(n => contentOf(n) === prevContent)
        }
        if (found !== -1) {
          if (found !== selectedIndex) setSelectedIndex(found)
        } else if (selectedIndex >= notes.length) {
          setSelectedIndex(null)
        }
        // Si no se encuentra (p.ej. escribiendo: el texto cambia en cada
        // pulsación), se conserva el índice actual.
      }
    } else if (selectedIndex !== null && selectedIndex >= notes.length) {
      setSelectedIndex(null)
    }
    prevNotesRef.current = notes
  }, [notes, selectedIndex])

  /* ─── Render ──────────────────────────────────────────────────────────── */

  return (
    <>
      {/* ── Lista de conversaciones ── */}
      <Col
        xs={12} lg={4}
        className="d-flex flex-column"
        style={{ borderRight: '1px solid #dee2e6', height: '72vh', overflow: 'hidden' }}
      >
        {/* Cabecera */}
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
          style={{ background: '#f8f9fa' }}>
          <span className="fw-semibold small">{t("Conversaciones")}</span>
          <Button type="button" size="sm" variant="primary" onClick={addThread} title={t("Nueva conversación")}>
            <Plus size={14} />
          </Button>
        </div>

        {/* Lista */}
        <ListGroup variant="flush" className="overflow-auto flex-grow-1">
          {notes.length === 0 && (
            <div className="text-center text-muted p-4 small">
              {t("No hay conversaciones todavía.")}
            </div>
          )}

          {notes.map((note: NoteThread, index: number) => (
            <ListGroup.Item
              as="button"
              type="button"
              action
              active={index === selectedIndex}
              key={index}
              onClick={() => setSelectedIndex(index)}
              className="border-0 border-bottom text-start px-3 py-2"
              style={{ borderLeft: index === selectedIndex ? '3px solid #0d6efd' : '3px solid transparent' }}
            >
              {/* Título + badge */}
              <div className="d-flex align-items-center gap-1 mb-1">
                {isFromEbroker(note) && (
                  <Badge bg="warning" text="dark" style={{ fontSize: '0.6rem', flexShrink: 0 }}>eb</Badge>
                )}
                {!note._id && (
                  <Badge bg="success" style={{ fontSize: '0.6rem', flexShrink: 0 }}>nueva</Badge>
                )}
                <span className="fw-semibold text-truncate" style={{ fontSize: '0.8rem' }}>
                  {note.titulo?.trim() || note.text?.substring(0, 40)?.trim() || t("Sin título")}
                </span>
              </div>

              {/* Preview texto */}
              {note.text && (
                <div className="text-truncate text-muted" style={{ fontSize: '0.75rem' }}>
                  {note.text.substring(0, 55)}
                </div>
              )}

              {/* Meta: autor + fecha + nº respuestas */}
              <div className="d-flex justify-content-between align-items-center mt-1">
                <span style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                  {authorName(note)}
                </span>
                <div className="d-flex align-items-center gap-2">
                  {(note.notes?.length ?? 0) > 0 && (
                    <Badge bg="secondary" pill style={{ fontSize: '0.65rem' }}>
                      {note.notes!.length}
                    </Badge>
                  )}
                  <span style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                    {formatDate(note.lastActivity || note.date)}
                  </span>
                </div>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Col>

      {/* ── Vista de chat ── */}
      <Col
        xs={12} lg={8}
        className="d-flex flex-column"
        style={{ height: '72vh', overflow: 'hidden' }}
      >
        {!selectedNote ? (
          /* Estado vacío */
          <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted gap-2">
            <MessageSquare size={42} strokeWidth={1} />
            <span className="small">{t("Selecciona una conversación")}</span>
          </div>
        ) : (
          <>
            {/* Cabecera del chat */}
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
              style={{ background: '#f8f9fa', flexShrink: 0 }}
            >
              <div className="d-flex align-items-center gap-2 min-w-0">
                {isFromEbroker(selectedNote) && (
                  <Badge bg="warning" text="dark" style={{ fontSize: '0.65rem', flexShrink: 0 }}>ebroker</Badge>
                )}
                <span className="fw-semibold text-truncate small">
                  {selectedNote.titulo?.trim() || selectedNote.text?.substring(0, 60) || t("Sin título")}
                </span>
              </div>
              {isEditable(selectedNote) && (
                <Trash2
                  size={16}
                  className="cursor text-danger ms-2"
                  style={{ flexShrink: 0, cursor: 'pointer' }}
                  onClick={e => { e.preventDefault(); deleteThread(selectedIndex!) }}
                />
              )}
            </div>

            {/* Mensajes */}
            <div className="flex-grow-1 overflow-auto p-3" style={{ background: '#fff' }}>

              {/* Mensaje principal del hilo */}
              {isEditable(selectedNote) ? (
                /* Nuevo hilo — formulario */
                <div style={bubbleNew} className="mb-3">
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder={t("Título (opcional)")}
                    value={selectedNote.titulo || ""}
                    onChange={e => updateThreadTitulo(e.target.value)}
                    className="mb-2 border-0 bg-transparent p-0 fw-semibold"
                    style={{ outline: 'none', boxShadow: 'none' }}
                  />
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder={t("Escribe el mensaje...")}
                    value={selectedNote.text || ""}
                    onChange={e => updateThreadText(e.target.value)}
                    className="border-0 bg-transparent p-0"
                    style={{ resize: 'none', outline: 'none', boxShadow: 'none', fontSize: '0.875rem' }}
                  />
                  <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                    {t("Nuevo — se enviará a ebroker al guardar")}
                  </div>
                </div>
              ) : (
                /* Hilo guardado — burbuja */
                <div className={`d-flex mb-3 ${isFromEbroker(selectedNote) ? 'justify-content-start' : 'justify-content-end'}`}>
                  <div style={isFromEbroker(selectedNote) ? bubbleEbroker : bubbleApp}>
                    {selectedNote.titulo?.trim() && (
                      <div className="fw-semibold mb-1" style={{ fontSize: '0.8rem' }}>
                        {selectedNote.titulo}
                      </div>
                    )}
                    <div>{selectedNote.text}</div>
                    <div
                      className="mt-1 d-flex justify-content-between gap-3"
                      style={{ fontSize: '0.68rem', opacity: 0.75 }}
                    >
                      <span>{authorName(selectedNote)}</span>
                      <span>{formatDate(selectedNote.date)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Divisor si hay respuestas */}
              {(selectedNote.notes?.length ?? 0) > 0 && (
                <div className="d-flex align-items-center gap-2 my-3">
                  <hr className="flex-grow-1 my-0" />
                  <span className="text-muted" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                    {selectedNote.notes!.length} {t("respuesta(s)")}
                  </span>
                  <hr className="flex-grow-1 my-0" />
                </div>
              )}

              {/* Respuestas */}
              {(selectedNote.notes ?? []).map((reply: NoteReply, ri: number) => (
                <div
                  key={ri}
                  className={`d-flex align-items-end gap-2 mb-3 ${isEditable(reply) ? 'justify-content-center' : isFromEbroker(reply) ? 'justify-content-start' : 'justify-content-end'}`}
                >
                  {isEditable(reply) ? (
                    /* Respuesta nueva — área de composición */
                    <div style={{ ...bubbleNew, width: '100%' }} className="d-flex gap-2">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder={t("Escribe la respuesta...")}
                        value={reply.text || ""}
                        onChange={e => updateReplyText(ri, e.target.value)}
                        className="border-0 bg-transparent p-0 flex-grow-1"
                        style={{ resize: 'none', outline: 'none', boxShadow: 'none', fontSize: '0.875rem' }}
                      />
                      <Trash2
                        size={15}
                        className="cursor text-danger flex-shrink-0 mt-1"
                        style={{ cursor: 'pointer' }}
                        onClick={e => { e.preventDefault(); deleteReply(ri) }}
                      />
                    </div>
                  ) : (
                    /* Respuesta guardada — burbuja */
                    <div style={isFromEbroker(reply) ? bubbleEbroker : bubbleApp}>
                      <div>{reply.text}</div>
                      <div
                        className="mt-1 d-flex justify-content-between gap-3"
                        style={{ fontSize: '0.68rem', opacity: 0.75 }}
                      >
                        <span>{authorName(reply)}</span>
                        <span>{formatDate(reply.date)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            {/* Barra inferior — solo para hilos guardados */}
            {!isEditable(selectedNote) && (
              <div
                className="px-3 py-2 border-top d-flex justify-content-end"
                style={{ background: '#f8f9fa', flexShrink: 0 }}
              >
                <Button type="button" size="sm" variant="primary" onClick={addReply}>
                  <Plus size={14} className="me-1" />
                  {t("Responder")}
                </Button>
              </div>
            )}
          </>
        )}
      </Col>
    </>
  )
}
